import { useState, useEffect } from 'react';
import { Lock, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const SESSION_KEY = (userId, pageKey) => `page_unlocked_${userId}_${pageKey}`;

export default function PagePasswordGate({ pageKey, pageLabel, children }) {
  const { user } = useAuth();
  const [status, setStatus]   = useState('checking'); // checking | locked | unlocked
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user) return;

    // Session madhe already unlocked ahe ka check karo
    const sessionKey = SESSION_KEY(user.id, pageKey);
    if (sessionStorage.getItem(sessionKey) === 'true') {
      setStatus('unlocked');
      return;
    }

    // Backend la check karo — ya page la password set ahe ka
    fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/backend'}/users/check-page-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('wqa_token')}`,
      },
      body: JSON.stringify({ user_id: user.id, page_key: pageKey }),
    })
      .then(r => r.json())
      .then(data => {
        if (data.data?.has_password) {
          setStatus('locked');
        } else {
          setStatus('unlocked'); // Password set nahi — direct access
        }
      })
      .catch(() => setStatus('unlocked')); // Error asel tar block nako
  }, [user, pageKey]);

  const submit = async () => {
    if (!password) return setError('Enter Password');
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/backend'}/users/verify-page-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('wqa_token')}`,
        },
        body: JSON.stringify({ user_id: user.id, page_key: pageKey, password }),
      });
      const data = await res.json();
      if (data.data?.valid) {
        sessionStorage.setItem(SESSION_KEY(user.id, pageKey), 'true');
        setStatus('unlocked');
      } else {
        setError('Chukicha password! Parat try kara.');
        setPassword('');
      }
    } catch {
      setError('Server error. Parat try kara.');
    } finally {
      setLoading(false);
    }
  };

  if (status === 'checking') {
    return <div style={{ padding:28, color:'var(--text2)', fontSize:13 }}>Loading...</div>;
  }

  if (status === 'unlocked') return children;

  // Password prompt screen
  return (
    <div style={{
      minHeight:'70vh', display:'flex', alignItems:'center', justifyContent:'center', padding:24,
    }}>
      <div style={{
        width:'100%', maxWidth:380,
        background:'var(--card)', border:'1px solid var(--border)',
        borderRadius:16, overflow:'hidden',
        boxShadow:'0 8px 32px rgba(0,0,0,0.18)',
      }}>
        {/* Header */}
        <div style={{
          background:'linear-gradient(135deg,#1a56db,#3b82f6)',
          padding:'28px 28px 24px',
          textAlign:'center',
        }}>
          <div style={{
            width:56, height:56, borderRadius:'50%',
            background:'rgba(255,255,255,0.15)',
            display:'flex', alignItems:'center', justifyContent:'center',
            margin:'0 auto 12px',
          }}>
            <Lock size={26} color="#fff" />
          </div>
          <h2 style={{ color:'#fff', fontSize:18, fontWeight:700, margin:0 }}>
            {pageLabel} — Protected
          </h2>
          <p style={{ color:'rgba(255,255,255,0.8)', fontSize:12, marginTop:6, marginBottom:0 }}>
            Please enter the password to access this page.
          </p>
        </div>

        {/* Form */}
        <div style={{ padding:28 }}>
          <div className="form-group" style={{ marginBottom:16 }}>
            <label style={{ fontSize:13, fontWeight:500, marginBottom:6, display:'block' }}>
              Page Password
            </label>
            <div style={{ position:'relative' }}>
              <input
                type={showPass ? 'text' : 'password'}
                value={password}
                onChange={e => { setPassword(e.target.value); setError(''); }}
                onKeyDown={e => e.key === 'Enter' && submit()}
                placeholder="Password..."
                autoFocus
                style={{ paddingRight:40, borderColor: error ? 'var(--danger)' : undefined }}
              />
              <button
                onClick={() => setShowPass(s => !s)}
                style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:'var(--text3)' }}
              >
                {showPass ? <EyeOff size={15}/> : <Eye size={15}/>}
              </button>
            </div>
            {error && (
              <div style={{ fontSize:12, color:'var(--danger)', marginTop:6 }}>
                ⚠️ {error}
              </div>
            )}
          </div>

          <button
            className="btn-primary"
            onClick={submit}
            disabled={loading}
            style={{ width:'100%', justifyContent:'center', display:'flex', alignItems:'center', gap:6 }}
          >
            <Lock size={14} />
            {loading ? 'Verifying...' : 'Unlock Page'}
          </button>

          <p style={{ fontSize:11, color:'var(--text3)', textAlign:'center', marginTop:12, marginBottom:0 }}>
            Ask your company admin for the password
          </p>
        </div>
      </div>
    </div>
  );
}